/**
 * @typedef {Object} BannerTextStyle
 * @property {string} [color] - Optional text color override, typically a hex value.
 * @property {number|string} [fontsize] - Optional font size override. A number is treated as
 *   pixels; a string may carry its own unit (e.g. "24px", "1.5rem").
 * @property {number|string} [fontweight] - Optional font-weight override. A number or a numeric
 *   string is used as-is; the CSS keywords normal/bold/lighter/bolder are also accepted.
 */

const FONT_SIZE_UNIT_PATTERN = /^\d+(\.\d+)?(px|rem|em|%|vw|vh)$/i;
const FONT_WEIGHT_KEYWORDS = ['normal', 'bold', 'lighter', 'bolder'];
const NUMERIC_FONT_WEIGHT_PATTERN = /^[1-9]00$/;

/**
 * Normalizes supported banner text style overrides into an inline style object.
 * Undefined or unsupported values are ignored so component classes remain the default.
 *
 * Booker divergence from upstream: the string branches below are required because the Sitecore
 * payload sends `fontsize` and `fontweight` as normalized strings (TextStyleHelper maps the
 * Font Weights droplist to numeric CSS weights). The numeric branches are unchanged from
 * upstream, so PromotionsBanner, ImageBannerNavigation and BannerWithCountdown are unaffected.
 * Keep this version when merging from booker-specific.
 *
 * @param {BannerTextStyle} [textstyle] - Optional banner text style overrides.
 * @returns {Object|undefined} CSS style object ready to pass to a text element.
 */
export const getBannerTextStyle = (textstyle) => {
  if (!textstyle || typeof textstyle !== 'object') {
    return undefined;
  }

  const style = {};

  if (typeof textstyle.color === 'string' && textstyle.color.trim()) {
    style.color = textstyle.color.trim();
  }

  if (Number.isFinite(textstyle.fontsize)) {
    style.fontSize = `${textstyle.fontsize}px`;
  } else if (typeof textstyle.fontsize === 'string') {
    const fontsize = textstyle.fontsize.trim();

    if (/^\d+(\.\d+)?$/.test(fontsize)) {
      style.fontSize = `${fontsize}px`;
    } else if (FONT_SIZE_UNIT_PATTERN.test(fontsize)) {
      style.fontSize = fontsize;
    }
  }

  if (Number.isFinite(textstyle.fontweight)) {
    style.fontWeight = textstyle.fontweight;
  } else if (typeof textstyle.fontweight === 'string') {
    const fontweight = textstyle.fontweight.trim().toLowerCase();

    if (
      FONT_WEIGHT_KEYWORDS.includes(fontweight) ||
      NUMERIC_FONT_WEIGHT_PATTERN.test(fontweight)
    ) {
      style.fontWeight = fontweight;
    }
  }

  return Object.keys(style).length > 0 ? style : undefined;
};
